import { supabase } from './supabase';

/**
 * Reads the access claims injected into the JWT by the Supabase Custom
 * Access Token Hook (see migration 015 + docs/jwt-claims-setup.md).
 *
 * Why this exists: Phase 2 moved access checks off per-row RLS subqueries
 * onto JWT app_metadata claims. Reading them client-side is a free
 * lookup (no network roundtrip), unlike the legacy fetchHasActiveAccess()
 * RPC which hits the DB.
 */

export interface AccessClaims {
  hasActiveAccess: boolean;
  userRole: 'user' | 'admin' | 'developer';
}

/**
 * Returns the access claims from the current session JWT, or null if
 * unauthenticated. If the JWT predates migration 015 + the hook config
 * (i.e. claims aren't in app_metadata yet), returns conservative defaults
 * (hasActiveAccess: false, userRole: 'user') so the caller can decide
 * whether to refresh + retry.
 */
export async function readAccessClaims(): Promise<AccessClaims | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  return parseClaimsFromAccessToken(session.access_token);
}

/**
 * Refresh the session and read the new claims. Call this when:
 *  - the user just returned from Stripe Checkout / Billing Portal
 *  - the user's claims appear stale (e.g. RequireAccess says no access
 *    but the user reports they just paid)
 *  - any client-side action that should immediately grant new permissions
 */
export async function refreshAccessClaims(): Promise<AccessClaims | null> {
  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session) return null;
  return parseClaimsFromAccessToken(data.session.access_token);
}

function parseClaimsFromAccessToken(token: string): AccessClaims {
  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
    const meta = payload?.app_metadata ?? {};
    return {
      hasActiveAccess: Boolean(meta.has_active_access),
      userRole: (meta.user_role as AccessClaims['userRole']) ?? 'user',
    };
  } catch {
    return { hasActiveAccess: false, userRole: 'user' };
  }
}

function decodeBase64Url(s: string): string {
  // JWT uses base64url which is base64 with `-` → `+`, `_` → `/`, no padding.
  const padded = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(s.length + ((4 - (s.length % 4)) % 4), '=');
  return atob(padded);
}
